import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { arrayMoveMutable } from 'array-move';
import { formatDate } from 'date-fns';
import { v4 as uuid } from 'uuid';

import { authActions } from '../auth/slice';

import { NewPlaylist, Playlist, PlaylistEntry, PlaylistState } from './types';
import { Resource, ResourceType } from '../resources/types';

const createBlankPlaylist = (): NewPlaylist => ({
  name: `Playlist ${formatDate(new Date(), "yyyy-MM-dd")}`,
  items: [],
  modified: false
});

const initialState: PlaylistState = {
  current: createBlankPlaylist(),
  savedPlaylists: {}
};

type AddItemInlinePayload = {
  index?: number,
  resource: Resource,
  themeID?: string
}

type AddItemReferencePayload = {
  index?: number,
  resourceID: string,
  themeID?: string,
  title: string,
  type: ResourceType
}

type AddItemPayload = AddItemInlinePayload | AddItemReferencePayload;

type MoveItemPayload = {
  fromIndex: number,
  toIndex?: number
}

type SetItemThemePayload = {
  index: number,
  themeID: string | undefined
}

type SaveCurrentPlaylistPayload = {
  name?: string
}

export const isReference = (thing: AddItemPayload): thing is AddItemReferencePayload => {
  return (thing as AddItemReferencePayload).resourceID !== undefined;
};

const playlistSlice = createSlice({
  name: 'playlist',
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<AddItemPayload>) => {

      const newItem: PlaylistEntry = isReference(action.payload) ? {
        resourceID: action.payload.resourceID,
        themeID: action.payload.themeID,
        title: action.payload.title,
        type: action.payload.type
      } : {
        resource: action.payload.resource,
        themeID: action.payload.themeID,
        title: action.payload.resource.title,
        type: action.payload.resource.type
      };

      const insertIndex = (action.payload.index === undefined) ? state.current.items.length : action.payload.index;

      state.current.items.splice(insertIndex, 0, newItem);
      state.current.modified = true;
    },
    clearPlaylist: (state) => {
      state.current = createBlankPlaylist();
    },
    deletePlaylist: (state, action: PayloadAction<string>) => {
      delete state.savedPlaylists[action.payload];
    },
    import: (state, action: PayloadAction<Playlist[]>) => {
      for (const playlist of action.payload) {
        if (playlist.id === 'CURRENT') {
          state.current = playlist;
        }
        else {
          state.savedPlaylists[playlist.id] = playlist;
        }
      }
    },
    loadPlaylist: (state, action: PayloadAction<string>) => {
      if (state.savedPlaylists[action.payload]) {
        state.current = {
          ...state.savedPlaylists[action.payload],
          originalID: action.payload,
          id: 'CURRENT'
        };
      }
    },
    moveItem: (state, action: PayloadAction<MoveItemPayload>) => {
      const { fromIndex } = action.payload;
      let toIndex = state.current.items.length;
      if (action.payload.toIndex !== undefined) {
        if (action.payload.toIndex > fromIndex) {
          toIndex = action.payload.toIndex - 1;
        }
        else {
          toIndex = action.payload.toIndex;
        }
      }
      arrayMoveMutable(state.current.items, fromIndex, toIndex);
      state.current.modified = true;
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.current.items.splice(action.payload, 1);
      state.current.modified = true;
    },
    saveCurrentPlaylist: (state, action: PayloadAction<SaveCurrentPlaylistPayload>) => {
      const id = state.current.originalID || uuid();
      const saveablePlaylist: Playlist = {
        name: action.payload.name || state.current.name,
        items: state.current.items,
        id,
        originalID: id,
        lastSavedAt: Date.now()
      };
      state.savedPlaylists[id] = saveablePlaylist;
      state.current.originalID = id;
      state.current.modified = false;
    },
    setItemTheme: (state, action: PayloadAction<SetItemThemePayload>) => {
      if (action.payload.themeID) {
        state.current.items[action.payload.index].themeID = action.payload.themeID;
      }
      else {
        delete state.current.items[action.payload.index].themeID;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(authActions.logOut, () => ({ ...initialState }));
  }
});

export const playlistActions = playlistSlice.actions;

export default playlistSlice.reducer;
